import { useRef } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { useEntityFinderContext } from "../context/EntityFinderContext";
import { Client } from "@/bryntum-lib/models/Client";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { ENTITY_TYPES } from "@/other/constants/OtherKeys";
import { getFontSize } from "@/react/context/bryntumContext";

export const ClientTab = () => {
  const { data, handleClose } = useEntityFinderContext();
  const { entityManager } = useSwivuBryntumContext();
  const clients = data as Client[];
  const containerRef = useRef<HTMLDivElement>(null);

  const gap = 12;
  const rowVirtualizer = useVirtualizer({
    count: clients.length,
    getScrollElement: () => containerRef.current,
    estimateSize: () => getFontSize() * 3 + gap,
    overscan: 8
  });

  const virtualItems = rowVirtualizer.getVirtualItems();
  const totalSize = rowVirtualizer.getTotalSize();

  const handleClientClick = (client: Client) => {
    entityManager.openEntityManager({ entityType: ENTITY_TYPES.CLIENT, entityId: client.id });
    handleClose();
  };

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <div ref={containerRef} className="flex-1 overflow-auto bg-background">
        {clients.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-muted-foreground p-8 text-center">
            <svg className="w-12 h-12 mb-4 opacity-40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
              />
            </svg>
            <p className="text-sm">No clients found</p>
          </div>
        ) : (
          <div className="relative w-full" style={{ height: `${totalSize}px` }}>
            {virtualItems.map((virtualRow) => {
              const client = clients[virtualRow.index];
              return (
                <div
                  key={client.id}
                  className="absolute left-0 top-0 w-full"
                  style={{
                    height: `${virtualRow.size}px`,
                    transform: `translateY(${virtualRow.start}px)`
                  }}
                >
                  <div
                    className="px-4 flex items-center bg-card hover:opacity-90 cursor-pointer transition-all rounded-md"
                    style={{ height: `${virtualRow.size - gap}px`, marginBottom: `${gap}px` }}
                    onClick={() => handleClientClick(client)}
                  >
                    <div className="font-medium text-foreground line-clamp-1">{client.name || "N/A"}</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
